/***********************************************
ICEMEDX - Developed By ICEMEDX, Inc.
Copyright 2022 ICEMEDX, Inc.
***********************************************/
import { Renderer2 } from '@angular/core';
import { IceMultimediaService } from '../../services/multimedia/icemultimedia.service';
import { Iceutility } from '../../services/iceutility';
import { IcecontrolSettings } from '../models/icesettings';
import { IceMultimediaAttributes } from './IceMultimediaAttributes';
import { multimediaModes, IceMultimediaPage, strokeColors } from './IceMultimediaPage';
import { IceMultimediaStroke, IcePoint } from './IceMultimediaStroke';
import { IceMultimediaText } from './IceMultimediaText';

export class IceMultimediaCanvas /*add redering base later*/{
  defaultAttributes: IceMultimediaAttributes = new IceMultimediaAttributes();
  control_id: string = "canvas_" + "_" + Iceutility.guid(true); // the Canvas ID
  page: IceMultimediaPage = null;
  currentStroke: IceMultimediaStroke = null;
  prevPoint: IcePoint = new IcePoint(0, 0);
  canvas_parent_id: string = "";

  container_id: string;
  context = null;
  m_rectBounding = null;

  // private fields
  private canvasdiv: any = null;
  private isDrawing: boolean = false;

  constructor(private parentControl: IcecontrolSettings,
    private renderer2: Renderer2,
    private grafService: IceMultimediaService,
    pg: IceMultimediaPage = null) {
    this.page = pg;
    this.container_id = "area_data_" + Iceutility.guid();
  }

  create(parentelem, tag) {
    if (Iceutility.isdefined(parentelem) == false) {
      return;
    }

    this.defaultAttributes.width = this.parentControl.properties.width == null || this.parentControl.properties.width == 0 ? 100 : this.parentControl.properties.width;
    this.defaultAttributes.height = this.parentControl.properties.height == null || this.parentControl.properties.height == 0 ? 100 : this.parentControl.properties.height;
    this.defaultAttributes.fillcolor = this.parentControl.properties.fillcolor == null || this.parentControl.properties.fillcolor == "" ? strokeColors.black : this.parentControl.properties.fillcolor;
    this.defaultAttributes.linewidth = this.parentControl.properties.linewidth == null || this.parentControl.properties.linewidth == 0 ? 2 : this.parentControl.properties.linewidth;
    this.defaultAttributes.font = this.parentControl.properties.font == null ? "12px sans-serif" : this.parentControl.properties.font;

    // this means tab layout is enabled so reduce the with on canvas
    // to avoid horizontal scroll
    if (this.parentControl.properties.showControlButtons) {

      this.defaultAttributes.width = Iceutility.isMobileDevice() ? window.outerWidth - 15 : this.defaultAttributes.width - 15;
      this.defaultAttributes.height = this.defaultAttributes.height - 100;
    }

    if (this.defaultAttributes.linewidth < 2) {
      this.defaultAttributes.linewidth = 2;
    } else if (this.defaultAttributes.linewidth > 5) {
      this.defaultAttributes.linewidth = 5;
    }

    if (this.parentControl.properties.minimalControl) {
      this.defaultAttributes.width = Iceutility.isMobileDevice() ? window.outerWidth : 180;
      this.defaultAttributes.height = 100;
    }

    try {
      // Create parent div for canvas
      this.canvas_parent_id = "parent_canvas_" + Iceutility.guid(true);
      this.canvasdiv = document.createElement("div");
      this.canvasdiv.setAttribute("iField", tag);
      this.canvasdiv.setAttribute("id", this.canvas_parent_id);
      this.canvasdiv.style.width = this.defaultAttributes.width.toString() + "px";
      this.canvasdiv.style.height = this.defaultAttributes.height.toString() + "px";
      
      // Create button container
      let btnContainer = document.createElement("div");
      btnContainer.style.position = "absolute";
      btnContainer.style.left = '0';
      this.canvasdiv.appendChild(btnContainer);

      // Create clear button
      if (!this.parentControl.properties.disabled) {
        let clearBtn = document.createElement("i");
        clearBtn.title = "Clear Canvas";
        clearBtn.style.margin = "0";
        clearBtn.style.fontSize = "20px";
        clearBtn.style.cursor = "pointer";
        clearBtn.classList.add("fas");
        clearBtn.classList.add("fa-eraser");
        btnContainer.appendChild(clearBtn);

        btnContainer.addEventListener("click", (event) => {
          this.clearCanvas(event);
        }, false);
      }

      // Canvas container
      let canvasContainer = document.createElement("div");
      this.canvasdiv.appendChild(canvasContainer);

      // Create canvas
      let canvas = document.createElement("canvas");
      canvas.setAttribute("id", this.control_id);
      canvas.setAttribute("iField", tag);
      canvas.setAttribute("width", this.defaultAttributes.width.toString());
      canvas.setAttribute("height", this.defaultAttributes.height.toString());
      if (!this.parentControl.properties.disabled) {
        canvas.style.cursor = "pointer";
      }
      if (this.parentControl.properties.disabled) {
        canvas.classList.add("disabled");
      }

      canvasContainer.appendChild(canvas);
      this.context = canvas.getContext("2d");
      if (this.page == null)
        this.page = this.grafService.newPage();

      if (Iceutility.isdefined(this.parentControl.properties.visible) && this.parentControl.properties.visible == false) {
        canvas.hidden = true;
      }

      // Add event handlers
      setTimeout(() => {
        this.renderer2.listen(canvas, "mousedown", event => {
          if (!this.parentControl.properties.disabled) {
            this.mouseDown(event);
          }
        });

        this.renderer2.listen(canvas, "mousemove", event => {
          if (!this.parentControl.properties.disabled) {
            this.mouseMove(event);
          }
        });

        this.renderer2.listen(canvas, "mouseup", event => {
          if (!this.parentControl.properties.disabled) {
            this.mouseUp(event);
          }
        });

        // touch events
        this.renderer2.listen(canvas, "touchstart", event => {
          if (!this.parentControl.properties.disabled) {
            event.preventDefault();
            this.touchStart(event);
          }
        });

        this.renderer2.listen(canvas, "touchmove", event => {
          if (!this.parentControl.properties.disabled) {
            event.preventDefault();
            this.touchMove(event);
          }
        });

        this.renderer2.listen(canvas, "touchend", event => {
          if (!this.parentControl.properties.disabled) {
            event.preventDefault();
            this.touchEnd(event);
          }
        });

        this.renderer2.listen(canvas, "click", event => {
          if (!this.parentControl.properties.disabled) {
            this.canvasClick(event);
          }
        });
      }, 500);
    } catch (e) {
      console.log(e);
    }

    return this.canvasdiv;
  };

  mouseDown(event) {
    let x = event.offsetX, y = event.offsetY,
      point = new IcePoint(x, y);
    // To do need to check if text is clicked on first, and if so it is a text box then do text mode or if data then do data mode (i.e. recording)
    // To do need to support all modes here (Erase, Text, Draw and Recorder)

    switch (this.page.defaultMode) {
      case multimediaModes.modeErase:
        this.isDrawing = true;
        break;
      case multimediaModes.modeText:
        break;
      case multimediaModes.modeRecorder:
        break;
      case multimediaModes.modeDraw:
      default:
        this.isDrawing = true;
        
        this.currentStroke = this.page.newStroke(this.defaultAttributes, point);
        break;
    }
    
    this.prevPoint = point;
  }

  mouseMove(event) {
    let x = event.offsetX, y = event.offsetY;
    let point = new IcePoint(x, y);

    switch (this.page.defaultMode) {
      case multimediaModes.modeErase:
        if (this.isDrawing) {
          this.page.deleteStroke(this, x, y);
        }
        break;
      case multimediaModes.modeText:
        break;
      case multimediaModes.modeRecorder:
        break;
      case multimediaModes.modeDraw:
      default:
        if (this.currentStroke != null && this.isDrawing) {
          this.page.draw(this, x, y);
        }
        break;
    }

    this.prevPoint = point;
  }

  mouseUp(event) {
    let x = event.offsetX, y = event.offsetY;
    let point = new IcePoint(x, y);
    // To do need to check if text is clicked on first, and then Start Edit

    if (this.currentStroke != null) {
      this.context.beginPath();
      this.context.moveTo(this.prevPoint.x, this.prevPoint.y);
      this.context.lineTo(point.x, point.y);
      this.context.closePath();
      this.context.stroke();
      this.currentStroke.pointArray.push(point);
      this.SetPageData();
      this.currentStroke = null;
    }
    this.prevPoint = point;
    this.isDrawing = false;
  }

  touchStart(event: any) {
    //if (this.page.defaultMode === this.page.modes.modeText) {
    //  this.canvasClick(event);
    //} else {
    let canvasElem = document.getElementById(this.control_id);
    let rect = canvasElem.getBoundingClientRect();
    const touches = event.changedTouches;
    for (let i = 0; i < touches.length; i++) {
      console.log(`touchstart: ${i}.`);

      let x = touches[i].pageX - window.pageXOffset - rect.left,
        y = touches[i].pageY - window.pageYOffset - rect.top;
      let point = new IcePoint(x, y);
      if (this.currentStroke == null)
        this.currentStroke = this.page.newStroke(this.defaultAttributes, point);
      else 
        this.currentStroke.pointArray.push(point);
      this.prevPoint = point;
    }
  }

  touchEnd(event) {
    let canvasElem = document.getElementById(this.control_id);
    let rect = canvasElem.getBoundingClientRect();
    const touches = event.changedTouches;
    for (let i = 0; i < touches.length; i++) {
      let point = new IcePoint(touches[i].pageX - window.pageXOffset - rect.left, touches[i].pageY - window.pageYOffset - rect.top);
      if (this.currentStroke != null) {
        this.context.beginPath();
        this.context.moveTo(this.prevPoint.x, this.prevPoint.y);
        this.context.lineTo(point.x, point.y);
        this.context.closePath();
        this.context.stroke();
        this.currentStroke.pointArray.push(point);
        this.SetPageData();
      }
      this.prevPoint = point;
    }
    this.currentStroke = null;
    
  }

  touchMove(event) {
    let canvasElem = document.getElementById(this.control_id);
    let rect = canvasElem.getBoundingClientRect();
    const touches = event.changedTouches;
    for (let i = 0; i < touches.length; i++) {
      let point = new IcePoint(touches[i].pageX - window.pageXOffset - rect.left, touches[i].pageY - window.pageYOffset - rect.top);
      if (this.currentStroke != null) {
        this.context.beginPath();
        this.context.moveTo(this.prevPoint.x, this.prevPoint.y);
        this.context.lineTo(point.x, point.y);
        this.context.closePath();
        this.context.stroke();
        this.currentStroke.pointArray.push(point);
        //this.parent.GetPageData();
        //this.parent.OnPageDraw.emit(this.parent.iceFormControl.value);
        this.prevPoint = point;
      }
    }
  }

  canvasClick(event) {
    if (this.page.defaultMode === multimediaModes.modeText) {
      this.NewText(event, this.control_id);
    }
  }

  clearCanvas(event) {
    let canvasElem: any = document.getElementById(this.control_id),
      context = canvasElem.getContext("2d");
    context.clearRect(0, 0, canvasElem.width, canvasElem.height);
    this.page.strokeList = [];
    this.page.textList = [];
    this.page.dataList = [];
    this.currentStroke = null;
    this.prevPoint = null;
    this.page.pageData = "";
    this.grafService.multimediaDataSub.next(this.page.pageData);
  }

  // Private methods

  private NewText(event, controlId) {
    try {

      let text = "",
        list: IceMultimediaText[] = this.page.textList,
        canvas = document.getElementById(controlId),
        x: number = null,
        y: number = null,
        leftOffset: number = canvas.offsetLeft,
        topOffset: number = canvas.offsetTop,
        maindiv: any = document.getElementById(this.container_id),
        button: any,
        br: any,
        textArea: any,
        width: number = 200,
        pageX: string = "0",
        pageY: string = "0",
        drawtext: boolean = false;

      if (event.touches && event.touches.length > 0) {
        x = event.touches[0].pageX - leftOffset;
        y = event.touches[0].pageY - topOffset;
      } else if (event.offsetX) {
        x = event.offsetX;
        y = event.offsetY;
      } else {
        x = event.pageX - event.target.offsetLeft;
        y = event.pageY - event.target.offsetTop;
      }

      // Check if cursor is on text
      for (let itext = 0; itext < list.length; itext++) {
        if (x >= list[itext].point.x && x <= (list[itext].point.x + list[itext].attributes.width) && y <= list[itext].point.y && y >= (list[itext].point.y - 10)) {
          text = list[itext].data;
          break;
        }
      }

      if (event.touches && event.touches.length > 0) {
        pageX = event.touches[0].pageX + "px";
        pageY = event.touches[0].pageY + "px";
      } else if (event.pageX == 0 && event.pageY == 0) {
        pageX = -(event.layerX) + "px";
        pageY = -(event.layerY) + "px";
      } else {
        pageX = event.pageX + "px";
        pageY = event.pageY + "px";
      }

      let left: string = pageX,
        top: string = pageY;

      // adjust the div position if it exceeds window width
      if (window.outerWidth < event.pageX + Iceutility.toLong(width)) {
        left = event.pageX - Iceutility.toLong(width) + "px";
      }

      if (Iceutility.isdefined(text) && text != "") {
        drawtext = true;
      }

      if (maindiv != null) {
        maindiv.remove();
      }

      // Create container for button and textarea
      maindiv = document.createElement("div");
      maindiv.setAttribute("id", this.container_id);
      maindiv.style.left = left;
      maindiv.style.top = top;
      maindiv.style.position = "absolute";
      maindiv.style.zIndex = 999999;

      button = document.createElement("button");
      button.innerText = "Done";
      button.setAttribute("id", "btnDone");
      button.style.padding = "2px";
      button.classList.add("mat-raised-button");
      maindiv.appendChild(button);

      button.addEventListener("click", (event) => {
        let x = 0,
          y = 0,
          text = maindiv.children[2].value,
          width = this.context.measureText(text).width;

        /// TODO: Check co-ordinates
        // Draw text
        //x = Iceutility.toLong(event.offsetX.replace("px", "")) - leftOffset;
        //y = Iceutility.toLong(event.offsetY.replace("px", "")) - topOffset;
        x = event.offsetX - leftOffset;
        y = event.offsetY - topOffset;

        if (drawtext) {
          let data = this.page.deleteText(this, x, y);
          if (Iceutility.isdefined(data)) {
            x = data.x;
            y = data.y;
          }
        }

        let attribs = new IceMultimediaAttributes();
        attribs.width = width;
        this.page.newText(attribs, x, y, text);
        this.page.draw(this, x, y, text);
        maindiv.remove();
      }, false);

      br = document.createElement("br");
      maindiv.appendChild(br);

      textArea = document.createElement("textarea");
      textArea.setAttribute("data-parent", controlId);
      textArea.style.width = width.toString() + "px";
      textArea.style.height = "80px";
      if (Iceutility.isdefined(text) && text != "") {
        textArea.value = text;
      }
      maindiv.appendChild(textArea);
      document.body.appendChild(maindiv);
      textArea.focus();

      document.addEventListener("keyup", (event) => {
        // Hide textarea if user presses escape key
        if (event.code == "Escape" && this.page.defaultMode == multimediaModes.modeText) {
          var div = document.getElementById(this.container_id);
          if (Iceutility.isdefined(div)) {
            div.className = "hidden";
          }
        }
      }, true);
    } catch (e) {
      console.log(e.message);
    }
  }

  private GetCurrentPoint() {
    let lastStroke = this.page.strokeList[this.page.strokeList.length - 1],
      currentPoint = new IcePoint(10, 10);

    if (Iceutility.isdefined(lastStroke) && Iceutility.isdefined(lastStroke.pointArray) && lastStroke.pointArray.length > 0) {
      currentPoint = lastStroke.pointArray[lastStroke.pointArray.length - 1];
    }

    return currentPoint;
  }

  private SetPageData() {
    this.page.pageData = this.grafService.serialize([this.page]);
    this.grafService.multimediaDataSub.next(this.page.pageData);
  }
}
